import { useEffect, useRef } from 'react'

import { useShallow } from 'zustand/react/shallow'

import { StoredReviewItem } from '../../../schemas/playbook'
import useAppStore from '../stores/store'

const highlightedTextMatchesReference = (
  ruleData: StoredReviewItem,
  highlightedText?: string
) => {
  const reference = ruleData.references?.find(
    (ref) => ref.id === ruleData.highlightedReferenceId
  )
  if (!reference) return false
  return highlightedText === reference.range?.text
}

/**
 *  This hook performs "selected text management" for a playbook rule.
 *  It does the following:
 *  - Selects the first reference for a rule when the rule is loaded.
 *  - Syncs the selected reference for a rule when the highlighted text changes.
 */
export const useInitialReference = (ruleData?: StoredReviewItem) => {
  const selectedInitialReference = useRef(false)

  const { highlightedText, markReferenceHighlighted } = useAppStore(
    useShallow((s) => ({
      highlightedText: s.highlightedText,
      markReferenceHighlighted: s.markReferenceHighlighted,
    }))
  )

  useEffect(() => {
    ;(async () => {
      if (
        !ruleData ||
        highlightedTextMatchesReference(ruleData, highlightedText?.text)
      )
        return

      if (selectedInitialReference.current) {
        markReferenceHighlighted(ruleData.ruleName, null)
        return
      }

      if (ruleData?.references?.length) {
        const firstReference = ruleData.references[0]

        if (firstReference.range) {
          firstReference.range.select()
          await firstReference.range.context.sync()
          markReferenceHighlighted(ruleData.ruleName, firstReference.id)
          selectedInitialReference.current = true
        }
      }
    })()
    // We ONLY want this to run once, hence disabling exhaustive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      ruleData &&
      !highlightedTextMatchesReference(ruleData, highlightedText?.text)
    ) {
      markReferenceHighlighted(ruleData.ruleName, null)
    }
    // There's a race condition between highlighted text updating & the highlighted reference ID being set
    // on render.
    // Easiest way to get around this is to only check when the highlighted text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedText?.text])
}
